<template>
  <a-dropdown :trigger="['click']" v-model:open="dropdownOpen" placement="bottomCenter" overlay-class-name="country-dropdown-overlay">
    <template #overlay>
      <div class="country-dropdown-container">
        <div class="country-search-wrapper">
          <a-input v-model:value="searchQuery" placeholder="Search" class="country-search-input">
            <template #prefix>
              <img :src="imagePath.searchIconGrey" alt="search_icon" class="search-icon" />
            </template>
          </a-input>
        </div>
        <a-menu class="country-menu" @click="handleCountrySelect">
          <a-menu-item v-for="country in filteredCountries" :key="country.code" :class="{ 'ant-menu-item-selected': selectedCountry.code === country.code }">
            <div class="country-menu-item">
              <span class="country-flag">{{ country.flag }}</span>
              <span class="country-name">{{ country.name }}</span>
            </div>
          </a-menu-item>
        </a-menu>
      </div>
    </template>
    <a-button class="primary_medium country-select-button">
      <div class="flex flex-row items-center gap-2">
        <span class="country-flag">{{ selectedCountry.flag }}</span>
        <span class="country-name">{{ selectedCountry.name }}</span>
        <img :src="imagePath.downArrowWhiteIcon" alt="dropdown arrow" class="dropdown-arrow" />
      </div>
    </a-button>
  </a-dropdown>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import imagePath from '@/javascript/imagePath';

const props = defineProps({
  countryOptions: {
    type: Array,
    required: true,
    default: () => [],
  },
  defaultCountry: {
    type: Object,
    default: () => ({ code: 'US', name: 'United States', dialCode: '+1', flag: '🇺🇸' }),
  },
});

const emit = defineEmits(['countryChange']);

const dropdownOpen = ref(false);
const searchQuery = ref('');
const selectedCountry = ref(props.defaultCountry);

watch(
  () => props.defaultCountry,
  (newCountry) => {
    if (newCountry) {
      selectedCountry.value = newCountry;
    }
  },
  { immediate: true }
);

const filteredCountries = computed(() => {
  if (!searchQuery.value) {
    return props.countryOptions;
  }
  const query = searchQuery.value.toLowerCase();
  return props.countryOptions.filter((country) => country.name.toLowerCase().includes(query) || country.dialCode.includes(query) || country.code.toLowerCase().includes(query));
});

const handleCountrySelect = ({ key }) => {
  const country = props.countryOptions.find((c) => c.code === key);
  if (country) {
    selectedCountry.value = country;
    dropdownOpen.value = false;
    searchQuery.value = '';
    emit('countryChange', country);
  }
};
</script>

<style scoped>
.country-select-button {
  display: flex;
  height: 50px;
  padding: 0 16px;
  justify-content: center;
  align-items: center;
  gap: 6px;
  border-radius: 24px;
  margin-top: 24px;
}

.country-flag {
  font-size: 16px;
  line-height: 1;
}

.country-name {
  font-size: 18px;
  font-weight: 600;
}

.dropdown-arrow {
  width: 16px;
  height: 16px;
  transition: transform 0.2s;
}

.country-dropdown-container {
  background: var(--dropdown-bg);
  border-radius: 8px;
  box-shadow: -2px 2px 24px 0 rgba(0, 0, 0, 0.4);
  overflow: hidden;
  min-width: 280px;
}

.country-search-wrapper {
  padding: 8px;
  border-bottom: 1px solid var(--white-opacity-8);
}

.country-search-input {
  background: var(--white-opacity-4) !important;
  border: 1px solid var(--white-opacity-8) !important;
  border-radius: 8px !important;
}

.country-search-input :deep(.ant-input) {
  background: transparent !important;
  color: var(--foreground-rgb) !important;
}

.country-search-input :deep(.ant-input-affix-wrapper) {
  background: transparent !important;
}

.country-search-input :deep(input::placeholder) {
  color: var(--white-opacity-20) !important;
}

.search-icon {
  width: 16px;
  height: 16px;
  opacity: 0.6;
}

.country-menu {
  background: transparent !important;
  border: none !important;
  max-height: 300px;
  overflow-y: auto;
  padding: 4px 0;
}

.country-menu-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0px 12px;
  width: 100%;
}

.country-menu-item .country-flag {
  font-size: 18px;
  width: 24px;
  text-align: center;
}

.country-menu-item .country-name {
  flex: 1;
  font-size: 14px;
  font-weight: 400;
  color: var(--foreground-rgb);
}

.country-menu-item .country-code {
  font-size: 14px;
  color: var(--secondary-color);
  font-weight: 400;
}

:deep(.ant-menu-item) {
  margin: 0 4px !important;
  border-radius: 8px !important;
  height: auto !important;
  line-height: normal !important;
  padding: 0 !important;
}

:deep(.ant-menu-item:hover) {
  background: var(--white-opacity-4) !important;
  border-radius: 8px !important;
}

:deep(.ant-menu-item-selected) {
  background: var(--white-opacity-10) !important;
  border-radius: 8px !important;
}

:deep(.ant-menu-item-selected::after) {
  display: none !important;
}

:deep(.country-dropdown-overlay) {
  left: 50% !important;
  transform: translateX(-50%) !important;
}

:deep(.ant-dropdown) {
  left: 50% !important;
  transform: translateX(-50%) !important;
  padding: 0 !important;
}

:deep(.ant-dropdown .country-dropdown-container) {
  margin: 0 auto;
}
</style>
